(() => {
  const DISMISSED_KEY = 'valesstra_welcome_popup_dismissed_at_v1';
  const SUBSCRIBED_KEY = 'valesstra_welcome_popup_subscribed_v1';
  const DISMISS_WINDOW = 7 * 24 * 60 * 60 * 1000;
  const MOBILE_MIN_DELAY = 20 * 1000;
  const MOBILE_FALLBACK_DELAY = 32 * 1000;
  const MOBILE_SCROLL_THRESHOLD = 0.5;

  const storage = {
    get(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch (error) {
        // Storage can be unavailable in private browsing; the popup still works for the session.
      }
    },
  };

  function ready(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  }

  ready(() => {
    const popup = document.querySelector('[data-valesstra-welcome-popup]');
    if (!popup || popup.dataset.initialized === 'true') return;
    popup.dataset.initialized = 'true';

    const params = new URLSearchParams(window.location.search);
    const testMode = params.get('popup_test') === '1';
    const dialog = popup.querySelector('[data-popup-dialog]');
    const emailInput = popup.querySelector('input[type="email"]');
    const successState = popup.querySelector('[data-popup-success]');
    const errorState = popup.querySelector('[data-popup-error]');
    const copyButton = popup.querySelector('[data-popup-copy]');
    const closeButtons = popup.querySelectorAll('[data-popup-close]');
    const startTime = Date.now();
    let previouslyFocused = null;
    let isOpen = false;
    let scheduled = false;
    let scrollHandler = null;
    let exitHandler = null;
    let openTimer = null;

    const normalizedPath = () => {
      const segments = window.location.pathname.split('/').filter(Boolean);
      const locale = (document.documentElement.lang || '').split('-')[0].toLowerCase();

      if (locale && segments[0] && segments[0].toLowerCase() === locale) {
        segments.shift();
      }

      return `/${segments.join('/')}` || '/';
    };

    const skipPath = () => {
      const path = normalizedPath();
      return (
        path === '/cart' ||
        path.startsWith('/account') ||
        path.startsWith('/challenge') ||
        path.startsWith('/password') ||
        path.startsWith('/discount/')
      );
    };

    const isSuppressed = () => {
      if (testMode) return false;
      if (storage.get(SUBSCRIBED_KEY) === '1') return true;
      const dismissedAt = Number(storage.get(DISMISSED_KEY));
      return Number.isFinite(dismissedAt) && dismissedAt > 0 && Date.now() - dismissedAt < DISMISS_WINDOW;
    };

    const competingOverlayOpen = () =>
      Boolean(
        document.querySelector(
          'cart-drawer.active, cart-drawer[open], .menu-drawer-container[open], details-modal[open], details-modal details[open], dialog[open], .search-modal[open], quick-add-modal[open], modal-dialog[open], .product-media-modal[open]'
        )
      );

    const focusableElements = () =>
      Array.from(
        dialog.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => !element.hidden && element.offsetParent !== null);

    const cleanupTriggers = () => {
      if (openTimer) window.clearTimeout(openTimer);
      if (scrollHandler) window.removeEventListener('scroll', scrollHandler);
      if (exitHandler) document.removeEventListener('mouseout', exitHandler);
      openTimer = null;
      scrollHandler = null;
      exitHandler = null;
    };

    const syncViewport = () => {
      const viewport = window.visualViewport;
      if (!window.matchMedia('(max-width: 749px)').matches || !viewport || viewport.scale > 1.1) {
        popup.style.removeProperty('--valesstra-popup-viewport-height');
        popup.style.removeProperty('--valesstra-popup-viewport-top');
        return;
      }
      popup.style.setProperty('--valesstra-popup-viewport-height', `${viewport.height}px`);
      popup.style.setProperty('--valesstra-popup-viewport-top', `${viewport.offsetTop}px`);
    };

    const onViewportChange = () => {
      if (isOpen) syncViewport();
    };

    window.addEventListener('resize', onViewportChange, { passive: true });
    window.visualViewport?.addEventListener('resize', onViewportChange, { passive: true });
    window.visualViewport?.addEventListener('scroll', onViewportChange, { passive: true });

    const show = ({ force = false } = {}) => {
      if (isOpen || (!force && isSuppressed())) return;
      if (!force && competingOverlayOpen()) {
        window.setTimeout(() => show(), 1500);
        return;
      }

      cleanupTriggers();
      previouslyFocused = document.activeElement;
      syncViewport();
      popup.hidden = false;
      popup.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('valesstra-welcome-popup-open');
      window.requestAnimationFrame(() => popup.classList.add('is-active'));
      isOpen = true;

      window.setTimeout(() => {
        if (!isOpen) return;
        const mobile = window.matchMedia('(max-width: 749px)').matches;
        const target = mobile
          ? dialog.querySelector('[data-popup-close]')
          : successState || errorState ? dialog.querySelector('a, button, input') : emailInput;
        if (target) target.focus({ preventScroll: true });
      }, 230);
    };

    const close = ({ remember = true } = {}) => {
      if (!isOpen) return;
      if (remember && !testMode && !successState) {
        storage.set(DISMISSED_KEY, String(Date.now()));
      }
      popup.classList.remove('is-active');
      popup.setAttribute('aria-hidden', 'true');
      isOpen = false;

      window.setTimeout(() => {
        popup.hidden = true;
        document.documentElement.classList.remove('valesstra-welcome-popup-open');
        if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
          previouslyFocused.focus({ preventScroll: true });
        }
      }, 230);
    };

    closeButtons.forEach((button) => button.addEventListener('click', () => close()));

    popup.addEventListener('click', (event) => {
      if (event.target.matches('[data-popup-overlay]')) close();
    });

    document.addEventListener('keydown', (event) => {
      if (!isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = focusableElements();
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    if (copyButton) {
      copyButton.addEventListener('click', async () => {
        const code = copyButton.dataset.popupCopy || 'WELCOME10';
        const copyLabel = copyButton.dataset.popupCopyLabel || 'Copy';
        const copiedLabel = copyButton.dataset.popupCopiedLabel || 'Copied';

        try {
          await navigator.clipboard.writeText(code);
          copyButton.textContent = copiedLabel;
          window.setTimeout(() => {
            copyButton.textContent = copyLabel;
          }, 1800);
        } catch (error) {
          copyButton.textContent = code;
        }
      });
    }

    if (successState) {
      storage.set(SUBSCRIBED_KEY, '1');
      storage.set(DISMISSED_KEY, String(Date.now()));
      show({ force: true });
      return;
    }

    if (errorState) {
      show({ force: true });
      return;
    }

    if (skipPath() || isSuppressed()) return;

    if (testMode) {
      window.setTimeout(() => show({ force: true }), 250);
      return;
    }

    const mobile = window.matchMedia('(max-width: 749px)').matches;
    scheduled = true;

    if (mobile) {
      openTimer = window.setTimeout(() => {
        if (scheduled) show();
      }, MOBILE_FALLBACK_DELAY);

      scrollHandler = () => {
        const scrollable = document.documentElement.scrollHeight - window.innerHeight;
        if (scrollable <= 0) return;
        const progress = window.scrollY / scrollable;
        if (progress < MOBILE_SCROLL_THRESHOLD) return;

        const elapsed = Date.now() - startTime;
        if (elapsed >= MOBILE_MIN_DELAY) {
          show();
          return;
        }

        if (openTimer) window.clearTimeout(openTimer);
        openTimer = window.setTimeout(() => {
          if (scheduled) show();
        }, MOBILE_MIN_DELAY - elapsed);
      };
      window.addEventListener('scroll', scrollHandler, { passive: true });
    } else {
      openTimer = window.setTimeout(() => {
        if (scheduled) show();
      }, 7000);

      exitHandler = (event) => {
        if (event.relatedTarget || event.clientY > 0 || Date.now() - startTime < 2000) return;
        show();
      };
      document.addEventListener('mouseout', exitHandler);
    }
  });
})();
